import { useEffect, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Fade,
  Grid,
  Stack,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { RecommendedActivityCard } from './components/activity-card';
import { ConfirmRedoDialog } from './components/confirm-redo-dialog';
import { ContentBox } from '@/components/shared/content-box';
import { ContentStream } from '@/components/shared/content-stream';
import { MarkdownRenderer } from '@/components/shared/markdown-renderer';
import { PulsingDot } from '@/components/shared/pulsing-dot';
import { IdpEvent } from '@/constants/analytics';
import { useNotification } from '@/context/notification';
import {
  RecommendationStreamingEvent,
  useIdpRecommendationStreaming,
} from '@/context/channels/profile-based-recommendation/streaming';
import { useWizard } from '@/context/wizard';
import { useShowUserFeedback } from '@/data/guidance/feedback';
import {
  useCompleteProfileBasedRecommendation,
  useRedoRecommendation,
} from '@/data/idp/profile-based-recommendations';
import {
  RecommendationPlanIds,
  type RecommendedPlanActivity,
  type SelectedActivityPayload,
} from '@/data/idp/profile-based-recommendations/interfaces';
import { useUpdatePlan } from '@/data/idp/plans';
import type { Course } from '@/models/catalog/course';
import {
  type CreatePlanWizardValues,
  FeedbackOptions,
} from '@/packages/idp/pages/create-plan/types';
import { track } from '@/utils/analytics';

const selectedActivitySchema = z.object({
  selected: z.boolean(),
  selected_course: z.custom<Course>().nullable(),
});

const formValidationSchema = z.object({
  selectedActivities: z.record(z.string(), selectedActivitySchema),
});

type FormValues = z.infer<typeof formValidationSchema>;

const TOKEN_DELAY_RANGE: [number, number] = [15, 25];

const toMarkdown = (text: string) =>
  text.replace(/(?<!\n)\n(?!\n)/g, '\n\n').replace(/([.!?])\s*/g, '$1\n\n');
export const RecommendationReview = () => {
  const navigate = useNavigate();
  const { values, actions } = useWizard<CreatePlanWizardValues>();
  const { userFeedback } = useShowUserFeedback();
  const { toast } = useNotification();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [textDone, setTextDone] = useState(false);
  const [visibleActivities, setVisibleActivities] = useState<
    RecommendedPlanActivity[]
  >([]);

  const hasStartedRef = useRef(false);

  const stepValues = values[RecommendationPlanIds.RecommendationReview];
  const existingActivities = stepValues?.recommended_activities ?? [];
  const existingDescription = stepValues?.recommended_plan_description ?? '';
  const hasExistingData = existingActivities.length > 0;

  const {
    status,
    activities: streamingActivities,
    planDescription,
    startStreaming,
  } = useIdpRecommendationStreaming({
    onCompleted: (streamedActivities, description) =>
      actions.updateValues({
        [RecommendationPlanIds.RecommendationReview]: {
          recommended_activities: streamedActivities,
          recommended_plan_description: description,
        },
      }),
    onError: () => {
      track(IdpEvent.AiFailed, {
        plan_id: values?.planId,
        stage: 'plan_generation',
      });
      toast({
        content: 'Erro ao gerar a recomendação. Tente novamente.',
        type: 'error',
      });
    },
  });

  const { handleSubmit, watch, setValue, reset } = useForm<FormValues>({
    defaultValues: { selectedActivities: {} },
    resolver: zodResolver(formValidationSchema),
    mode: 'onChange',
  });

  const allActivities = hasExistingData
    ? existingActivities
    : streamingActivities;
  const displayText = toMarkdown(
    hasExistingData ? existingDescription : planDescription
  );
  const selectedActivities = watch('selectedActivities') ?? {};

  const isReady =
    hasExistingData || status === RecommendationStreamingEvent.Completed;
  const isError = status === RecommendationStreamingEvent.Error;
  const showTextDot = !hasExistingData && !displayText;
  const showActivityDot = textDone && visibleActivities.length === 0;

  const {
    mutate: completeProfileBasedRecommendation,
    isPending: isCreatingPlan,
  } = useCompleteProfileBasedRecommendation();
  const { mutate: updatePlan } = useUpdatePlan();
  const { mutate: redoRecommendation, isPending: isRedoing } =
    useRedoRecommendation();

  const toggleActivity = (activityId: string, selected?: boolean) => {
    const current = selectedActivities[activityId];
    setValue(`selectedActivities.${activityId}`, {
      selected: selected ?? !current?.selected,
      selected_course:
        selected === false ? null : (current?.selected_course ?? null),
    });
  };

  const setCourseForActivity = (activityId: string, course: Course) => {
    setValue(`selectedActivities.${activityId}`, {
      selected: true,
      selected_course: course,
    });
  };

  const handleNext = (formData: FormValues) => {
    const activities: SelectedActivityPayload[] = Object.entries(
      formData.selectedActivities
    )
      .filter(([, activity]) => activity.selected)
      .map(([id, activity]) => ({
        id,
        selected_course: activity.selected_course ?? null,
      }));

    const feedbackOption = values?.[RecommendationPlanIds.Feedback]?.option;
    const feedbackIds =
      (feedbackOption === FeedbackOptions.Current ||
        feedbackOption === FeedbackOptions.New) &&
      userFeedback?.id
        ? [userFeedback.id]
        : undefined;

    completeProfileBasedRecommendation(
      {
        id: values?.id,
        currentStep: RecommendationPlanIds.RecommendationReview,
        activities,
      },
      {
        onSuccess: (responseData) => {
          const planId = responseData?.plan?.id;
          track(IdpEvent.PlanCreated, {
            plan_id: planId,
            creation_mode: 'ai',
            duration_months: values?.[RecommendationPlanIds.Duration],
            skills_count:
              values?.[RecommendationPlanIds.SkillSelection]?.length,
            activities_suggested: allActivities.length,
            activities_kept: activities.length,
            courses_attached: activities.filter(
              (activity) => activity.selected_course
            ).length,
          });
          if (feedbackIds && planId)
            updatePlan({ id: planId, feedback_ids: feedbackIds });
          navigate(`/pdi/planos/${planId}`);
        },
        onError: () =>
          toast({
            content: 'Ocorreu um erro ao criar o plano. Tente novamente.',
            type: 'error',
          }),
      }
    );
  };

  const handleRedo = () => {
    redoRecommendation(values?.id, {
      onSuccess: (responseData) => {
        track(IdpEvent.RecommendationRedone, { plan_id: values?.planId });
        setIsConfirmModalOpen(false);
        navigate(`/pdi/planos/novo/${responseData?.plan?.id}`);
      },
      onError: () =>
        toast({
          content: 'Erro ao refazer a recomendação. Tente novamente.',
          type: 'error',
        }),
    });
  };

  const handleRetryStreaming = () => {
    hasStartedRef.current = false;
    startStreaming(values.id);
  };

  useEffect(() => {
    if (!hasExistingData && values?.id && !hasStartedRef.current) {
      hasStartedRef.current = true;
      startStreaming(values.id);
    }
  }, [values?.id, hasExistingData, startStreaming]);

  useEffect(() => {
    if (!textDone && displayText) return;
    if (allActivities.length > visibleActivities.length) {
      const timer = setTimeout(() => {
        const next = allActivities.slice(0, visibleActivities.length + 1);
        setVisibleActivities(next);
        reset({
          selectedActivities: Object.fromEntries(
            next.map((activity) => [
              activity.id,
              { selected: true, selected_course: null },
            ])
          ),
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [textDone, displayText, allActivities, visibleActivities.length, reset]);

  if (isError) {
    return (
      <ContentBox gap={2} alignItems="center">
        <Typography variant="h2">Algo deu errado</Typography>
        <Typography>
          Não foi possível gerar sua recomendação. Verifique sua conexão e tente
          novamente.
        </Typography>
        <Button variant="contained" onClick={handleRetryStreaming}>
          Tentar novamente
        </Button>
      </ContentBox>
    );
  }

  return (
    <ContentBox mt={2} gap={3}>
      <Typography variant="h2">
        Aqui está a nossa recomendação de plano para você! 🎉
      </Typography>

      {showTextDot && <PulsingDot />}

      {displayText && (
        <ContentStream
          content={displayText}
          splitBy="word"
          tokenDelayRange={TOKEN_DELAY_RANGE}
          onEnd={() => setTextDone(true)}
        >
          {({ markdownContent }) => (
            <MarkdownRenderer markdown={markdownContent} />
          )}
        </ContentStream>
      )}

      <form onSubmit={handleSubmit(handleNext)}>
        <ToggleButtonGroup sx={{ width: '100%', display: 'block' }}>
          <Grid container spacing={2}>
            {showActivityDot && (
              <Grid size={12}>
                <PulsingDot />
              </Grid>
            )}
            {visibleActivities.map((activity) => {
              const activityFormValue = selectedActivities[activity.id];
              return (
                <Fade key={activity.id} in timeout={400}>
                  <Grid size={12}>
                    <RecommendedActivityCard
                      activity={activity}
                      isChecked={activityFormValue?.selected ?? true}
                      selectedCourse={activityFormValue?.selected_course}
                      onToggle={(selected) =>
                        toggleActivity(activity.id, selected)
                      }
                      onSelectCourse={(course) =>
                        setCourseForActivity(activity.id, course)
                      }
                    />
                  </Grid>
                </Fade>
              );
            })}
          </Grid>
        </ToggleButtonGroup>

        <Stack direction="row" justifyContent="flex-end" mt={4} spacing={2}>
          <Button
            disabled={!isReady || isRedoing}
            loading={isRedoing}
            onClick={() => setIsConfirmModalOpen(true)}
          >
            Refazer a recomendação
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isReady || isRedoing}
            loading={isCreatingPlan}
          >
            Criar Plano
          </Button>
        </Stack>
      </form>

      {isConfirmModalOpen && (
        <ConfirmRedoDialog
          isLoading={isRedoing}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleRedo}
        />
      )}
    </ContentBox>
  );
};
